(function () {
  'use strict';

  const PAGE_SELECTOR = '[data-koh-wishlist-page]';
  const instances = new WeakMap();

  const createElement = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const parseConfig = (page) => {
    const script = page.querySelector('[data-koh-wishlist-config]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const normalizedRoot = (value) => {
    const root = String(value || '/');
    return root.endsWith('/') ? root : `${root}/`;
  };

  const productEndpoint = (rootUrl, handle) => `${normalizedRoot(rootUrl)}products/${encodeURIComponent(handle)}.js`;
  const productUrl = (rootUrl, handle) => `${normalizedRoot(rootUrl)}products/${encodeURIComponent(handle)}`;

  const resizedImageUrl = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const responsiveImage = (source, alt, className, sizes, ratio) => {
    const image = createElement('img', className);
    image.src = resizedImageUrl(source, 720);
    image.srcset = [360, 540, 720, 960].map((width) => `${resizedImageUrl(source, width)} ${width}w`).join(', ');
    image.sizes = sizes;
    image.alt = alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    if (ratio !== 'original') {
      image.width = 720;
      image.height = ratio === '1-1' ? 720 : ratio === '3-4' ? 960 : 900;
    }
    return image;
  };

  const isProductAvailable = (product) => Boolean(
    product && (product.available === true || (Array.isArray(product.variants) && product.variants.some((variant) => variant.available)))
  );

  const hasSelectableOptions = (product) => {
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return true;
    if (product.variants.length > 1) return true;
    const title = String(product.variants[0].title || '');
    return title !== '' && title !== 'Default Title';
  };

  const mapWithConcurrency = async (items, limit, worker) => {
    const output = new Array(items.length);
    let cursor = 0;
    const workers = Array.from({ length: Math.min(Math.max(1, limit), Math.max(1, items.length)) }, async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        output[index] = await worker(items[index], index);
      }
    });
    await Promise.all(workers);
    return output;
  };

  class WishlistPage {
    constructor(page, config) {
      this.page = page;
      this.config = config;
      this.texts = config.texts || {};
      this.cache = new Map();
      this.results = new Map();
      this.renderVersion = 0;
      this.abortController = new AbortController();
      this.suppressNextChange = false;
      this.pendingUndo = null;
      this.undoTimer = 0;
      this.dialogTrigger = null;
      this.grid = page.querySelector('[data-koh-wishlist-grid]');
      this.loading = page.querySelector('[data-koh-wishlist-loading]');
      this.empty = page.querySelector('[data-koh-wishlist-empty]');
      this.error = page.querySelector('[data-koh-wishlist-error]');
      this.errorText = page.querySelector('[data-koh-wishlist-error-text]');
      this.clearButton = page.querySelector('[data-koh-wishlist-clear]');
      this.toolbar = page.querySelector('[data-koh-wishlist-toolbar]');
      this.titleCount = page.querySelector('[data-koh-wishlist-title-count]');
      this.sort = page.querySelector('[data-koh-wishlist-sort]');
      this.stockFilter = page.querySelector('[data-koh-wishlist-stock-filter]');
      this.clearDialog = page.querySelector('[data-koh-wishlist-clear-dialog]');
      this.clearMessage = page.querySelector('[data-koh-wishlist-clear-message]');
      this.undo = page.querySelector('[data-koh-wishlist-undo]');
      this.undoMessage = page.querySelector('[data-koh-wishlist-undo-message]');
      this.valid = Boolean(this.grid && this.loading && this.empty && this.clearButton);
    }

    initialize() {
      if (!this.valid || !window.KohWishlist) return;
      this.pauseAutoplayForReducedMotion();
      this.loadProducts();
    }

    destroy() {
      this.renderVersion += 1;
      this.abortController.abort();
      window.clearTimeout(this.undoTimer);
      this.closeClearDialog(false);
    }

    getItems() {
      return window.KohWishlist ? window.KohWishlist.getItems() : [];
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.config.locale || undefined, {
          style: 'currency',
          currency: this.config.currency || 'MYR',
        }).format(amount);
      } catch (error) {
        return `${this.config.currency || 'MYR'} ${amount.toFixed(2)}`;
      }
    }

    updateCount(count) {
      if (this.titleCount) this.titleCount.textContent = `(${count})`;
    }

    updateBlocks(state, count) {
      this.page.querySelectorAll('[data-koh-wishlist-block]').forEach((block) => {
        const visibility = block.dataset.visibility || 'always';
        const shouldShow = state !== 'loading' && (
          visibility === 'always' ||
          (visibility === 'empty_only' && count === 0) ||
          (visibility === 'has_items_only' && count > 0)
        );
        block.hidden = !shouldShow;
      });
    }

    setState(state, count, errorCount) {
      const hasItems = count > 0;
      this.page.dataset.kohWishlistState = state;
      this.page.toggleAttribute('data-koh-wishlist-partial-error', Boolean(errorCount && errorCount < count));
      this.loading.hidden = state !== 'loading';
      this.empty.hidden = state !== 'empty';
      this.grid.hidden = !hasItems || state === 'loading';
      this.clearButton.hidden = !hasItems || state === 'loading';
      if (this.toolbar) this.toolbar.hidden = !hasItems || state === 'loading';
      if (this.error) this.error.hidden = !errorCount;
      this.updateCount(count);
      this.updateBlocks(state, count);
    }

    async fetchProduct(item, force) {
      const cacheKey = String(item.handle);
      if (force) this.cache.delete(cacheKey);
      if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

      const request = fetch(productEndpoint(this.config.rootUrl, item.handle), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .then((product) => {
          if (!product || typeof product !== 'object' || !product.title || !product.handle) {
            throw new Error('Invalid product response');
          }
          return { item, product, ok: true, error: null };
        })
        .catch((error) => ({ item, product: null, ok: false, error }));

      this.cache.set(cacheKey, request);
      return request;
    }

    async loadProducts(options) {
      if (!this.valid || !window.KohWishlist) return;
      const currentVersion = this.renderVersion + 1;
      this.renderVersion = currentVersion;
      const items = this.getItems();
      this.updateCount(items.length);

      if (items.length === 0) {
        this.results.clear();
        this.grid.replaceChildren();
        this.setState('empty', 0, 0);
        return;
      }

      this.setState('loading', items.length, 0);
      const forceHandles = options && options.forceHandles instanceof Set ? options.forceHandles : new Set();
      const results = await mapWithConcurrency(items, Number(this.config.concurrency) || 4, (item) => (
        this.fetchProduct(item, forceHandles.has(item.handle))
      ));

      if (!this.page.isConnected || this.renderVersion !== currentVersion) return;
      const latestItems = this.getItems();
      if (latestItems.map((item) => item.id).join('|') !== items.map((item) => item.id).join('|')) {
        this.loadProducts();
        return;
      }

      this.results = new Map(results.map((result) => [String(result.item.id), result]));
      this.renderCards(items);
      const errorCount = results.filter((result) => !result.ok).length;
      if (this.errorText && errorCount) this.errorText.textContent = this.texts.networkError || '';
      this.setState(errorCount === items.length ? 'error' : 'has-items', items.length, errorCount);
    }

    sortedResults(items) {
      const resultList = items.map((item, index) => {
        const result = this.results.get(String(item.id)) || { item, product: null, ok: false };
        return Object.assign({}, result, { storageIndex: index });
      });
      const sortValue = this.sort ? this.sort.value : 'recent';
      const timestamp = (result) => Number(result.item.addedAt) || result.storageIndex;
      const price = (result) => result.ok ? Number(result.product.price || 0) : Number.POSITIVE_INFINITY;
      const title = (result) => result.ok ? String(result.product.title || '') : String(result.item.handle || '');

      if (sortValue === 'oldest') resultList.sort((a, b) => timestamp(a) - timestamp(b));
      else if (sortValue === 'price-asc') resultList.sort((a, b) => price(a) - price(b));
      else if (sortValue === 'price-desc') resultList.sort((a, b) => price(b) - price(a));
      else if (sortValue === 'title') resultList.sort((a, b) => title(a).localeCompare(title(b), this.config.locale || undefined));
      else resultList.sort((a, b) => timestamp(b) - timestamp(a));

      if (this.stockFilter && this.stockFilter.checked) {
        return resultList.filter((result) => result.ok && isProductAvailable(result.product));
      }
      return resultList;
    }

    renderCards(items) {
      const fragment = document.createDocumentFragment();
      this.sortedResults(items).forEach((result) => {
        fragment.appendChild(result.ok ? this.renderProductCard(result) : this.renderUnavailableCard(result));
      });
      this.grid.replaceChildren(fragment);
    }

    renderUnavailableCard(result) {
      const card = createElement('li', 'koh-wishlist-card koh-wishlist-card--unavailable');
      card.dataset.kohWishlistItem = result.item.id;
      card.dataset.kohWishlistStorageIndex = String(result.storageIndex);
      const media = createElement('div', 'koh-wishlist-card__media');
      const content = createElement('div', 'koh-wishlist-card__content');
      const title = createElement('h2', 'koh-wishlist-card__title', String(result.item.handle || '').replace(/-/g, ' '));
      const status = createElement('p', 'koh-wishlist-card__status', this.texts.unavailable || '');
      const actions = createElement('div', 'koh-wishlist-card__actions');
      const retry = createElement('button', 'button-secondary koh-wishlist-card__action', this.texts.retry || '');
      retry.type = 'button';
      retry.dataset.kohWishlistRetry = result.item.handle;
      retry.dataset.kohWishlistRetryId = result.item.id;
      actions.appendChild(retry);
      if (this.config.showRemove !== false) actions.appendChild(this.createRemoveButton(result.item.id));
      content.append(title, status, actions);
      card.append(media, content);
      return card;
    }

    renderProductCard(result) {
      const product = result.product;
      const url = productUrl(this.config.rootUrl, product.handle || result.item.handle);
      const card = createElement('li', 'koh-wishlist-card');
      card.dataset.kohWishlistItem = result.item.id;
      card.dataset.kohWishlistStorageIndex = String(result.storageIndex);

      const mediaLink = createElement('a', 'koh-wishlist-card__media');
      mediaLink.href = url;
      mediaLink.setAttribute('aria-label', product.title);
      const images = Array.isArray(product.images) ? product.images : [];
      const primarySource = product.featured_image || images[0];
      if (primarySource) {
        mediaLink.appendChild(responsiveImage(
          primarySource,
          product.title,
          'koh-wishlist-card__image koh-wishlist-card__image--primary',
          '(min-width: 990px) 25vw, 50vw',
          this.config.imageRatio
        ));
        if (this.config.hoverSecondImage && images[1]) {
          mediaLink.appendChild(responsiveImage(
            images[1],
            '',
            'koh-wishlist-card__image koh-wishlist-card__image--secondary',
            '(min-width: 990px) 25vw, 50vw',
            this.config.imageRatio
          ));
        }
      }

      const available = isProductAvailable(product);
      if (!available && this.config.showSoldOutBadge) {
        mediaLink.appendChild(createElement('span', 'koh-wishlist-card__badge', this.texts.soldOut || ''));
      }

      const content = createElement('div', 'koh-wishlist-card__content');
      if (this.config.showVendor && product.vendor) content.appendChild(createElement('p', 'koh-wishlist-card__vendor', product.vendor));
      if (this.config.showTitle !== false) {
        const title = createElement('h2', 'koh-wishlist-card__title');
        const titleLink = createElement('a', '', product.title);
        titleLink.href = url;
        title.appendChild(titleLink);
        content.appendChild(title);
      }

      const productPrice = Number(product.price || 0);
      const comparePrice = Number(product.compare_at_price || 0);
      if (this.config.showPrice !== false) {
        const price = createElement('p', 'koh-wishlist-card__price');
        price.appendChild(createElement('span', 'koh-wishlist-card__current-price', this.formatMoney(productPrice)));
        if (this.config.showComparePrice && comparePrice > productPrice) {
          price.appendChild(createElement('s', 'koh-wishlist-card__compare-price', this.formatMoney(comparePrice)));
        }
        if (this.config.showDiscount && comparePrice > productPrice && comparePrice > 0) {
          const discount = Math.round((1 - productPrice / comparePrice) * 100);
          price.appendChild(createElement('span', 'koh-wishlist-card__discount', `-${discount}%`));
        }
        content.appendChild(price);
      }

      if (this.config.showInventory) {
        content.appendChild(createElement('p', `koh-wishlist-card__status koh-wishlist-card__status--${available ? 'available' : 'sold-out'}`, available ? this.texts.inStock : this.texts.soldOut));
      }

      if (this.config.showAction !== false || this.config.showRemove !== false) {
        const actions = createElement('div', 'koh-wishlist-card__actions');
        if (this.config.showAction !== false) {
          if (available && !hasSelectableOptions(product) && product.variants && product.variants[0] && product.variants[0].available) {
            actions.appendChild(this.createProductForm(product, product.variants[0]));
          } else {
            const actionLabel = available ? this.texts.chooseOptions : this.texts.viewProduct;
            const viewLink = createElement('a', 'button koh-wishlist-card__action', actionLabel || '');
            viewLink.href = url;
            actions.appendChild(viewLink);
          }
        }
        if (this.config.showRemove !== false) actions.appendChild(this.createRemoveButton(result.item.id));
        content.appendChild(actions);
      }

      card.append(mediaLink, content);
      return card;
    }

    createRemoveButton(id) {
      const button = createElement('button', 'button-unstyled koh-wishlist-card__remove', this.texts.remove || '');
      button.type = 'button';
      button.dataset.kohWishlistRemove = id;
      return button;
    }

    createProductForm(product, variant) {
      const component = createElement('product-form-component', 'koh-wishlist-card__product-form');
      component.dataset.sectionId = this.config.sectionId || '';
      component.dataset.productId = String(product.id || '');
      component.dataset.productUrl = productUrl(this.config.rootUrl, product.handle);
      component.dataset.quantityDefault = '1';
      component.setAttribute('on:submit', '/handleSubmit');

      const live = createElement('div', 'visually-hidden');
      live.setAttribute('aria-live', 'assertive');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-atomic', 'true');
      live.setAttribute('ref', 'liveRegion');

      const form = createElement('form');
      form.method = 'post';
      form.action = this.config.cartAddUrl || `${normalizedRoot(this.config.rootUrl)}cart/add`;
      form.enctype = 'multipart/form-data';
      form.dataset.type = 'add-to-cart-form';
      const input = createElement('input');
      input.type = 'hidden';
      input.name = 'id';
      input.value = String(variant.id);
      input.setAttribute('ref', 'variantId');

      const error = createElement('span', 'koh-wishlist-card__form-error hidden');
      error.setAttribute('ref', 'addToCartTextError');
      const addComponent = createElement('add-to-cart-component');
      addComponent.setAttribute('ref', 'addToCartButtonContainer');
      const button = createElement('button', 'button koh-wishlist-card__action', this.texts.addToBag || '');
      button.type = 'submit';
      button.name = 'add';
      button.setAttribute('ref', 'addToCartButton');
      button.setAttribute('on:click', '/handleClick');
      const added = createElement('span', 'add-to-cart__added');
      const addedText = createElement('span', 'visually-hidden add-to-cart-text--added', this.texts.addedToBag || '');
      added.appendChild(addedText);
      button.appendChild(added);
      addComponent.appendChild(button);
      form.append(input, error, addComponent);
      component.append(live, form);
      return component;
    }

    handleClick(event, target) {
      const removeButton = target.closest('[data-koh-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeItem(removeButton.dataset.kohWishlistRemove, removeButton);
        return true;
      }
      const retry = target.closest('[data-koh-wishlist-retry]');
      if (retry) {
        event.preventDefault();
        this.loadProducts({ forceHandles: new Set([retry.dataset.kohWishlistRetry]) });
        return true;
      }
      if (target.closest('[data-koh-wishlist-retry-all]')) {
        event.preventDefault();
        const failed = new Set(Array.from(this.results.values()).filter((result) => !result.ok).map((result) => result.item.handle));
        this.loadProducts({ forceHandles: failed });
        return true;
      }
      if (target.closest('[data-koh-wishlist-clear]')) {
        event.preventDefault();
        this.openClearDialog(target.closest('[data-koh-wishlist-clear]'));
        return true;
      }
      if (target.closest('[data-koh-wishlist-clear-cancel]')) {
        event.preventDefault();
        this.closeClearDialog(true);
        return true;
      }
      if (target.closest('[data-koh-wishlist-clear-confirm]')) {
        event.preventDefault();
        this.confirmClear();
        return true;
      }
      if (target.closest('[data-koh-wishlist-undo-button]')) {
        event.preventDefault();
        this.undoRemove();
        return true;
      }
      if (target.closest('[data-koh-wishlist-undo-close]')) {
        event.preventDefault();
        this.dismissUndo();
        return true;
      }
      return false;
    }

    handleControlChange(target) {
      if (target.matches('[data-koh-wishlist-sort], [data-koh-wishlist-stock-filter]')) {
        this.renderCards(this.getItems());
      }
    }

    handleWishlistChange(event) {
      if (this.suppressNextChange) return;
      if (event && event.detail && event.detail.source === `wishlist-page-${this.config.sectionId}`) return;
      this.dismissUndo();
      this.loadProducts();
    }

    removeItem(id, trigger) {
      const itemId = String(id || '');
      const card = trigger.closest('[data-koh-wishlist-item]');
      const nextSibling = card ? card.nextSibling : null;
      const parent = card ? card.parentNode : this.grid;
      const cachedResult = this.results.get(itemId);
      this.suppressNextChange = true;
      const removed = window.KohWishlist.remove(itemId, { silent: true, source: `wishlist-page-${this.config.sectionId}` });
      this.suppressNextChange = false;
      if (!removed) return;

      if (card) card.remove();
      this.results.delete(itemId);
      const remainingCount = this.getItems().length;
      const remainingErrors = Array.from(this.results.values()).filter((result) => !result.ok).length;
      this.updateCount(remainingCount);
      this.setState(remainingCount === 0 ? 'empty' : (remainingErrors === remainingCount ? 'error' : 'has-items'), remainingCount, remainingErrors);
      this.showUndo({ item: removed.item, index: removed.index, card, parent, nextSibling, cachedResult });
    }

    showUndo(record) {
      this.dismissUndo();
      this.pendingUndo = record;
      if (!this.undo) return;
      if (this.undoMessage) this.undoMessage.textContent = this.texts.removed || '';
      this.undo.hidden = false;
      this.undoTimer = window.setTimeout(() => this.dismissUndo(), 6000);
    }

    dismissUndo() {
      window.clearTimeout(this.undoTimer);
      this.undoTimer = 0;
      this.pendingUndo = null;
      if (this.undo) this.undo.hidden = true;
    }

    undoRemove() {
      const record = this.pendingUndo;
      if (!record) return;
      window.clearTimeout(this.undoTimer);
      this.suppressNextChange = true;
      const restored = window.KohWishlist.restore(record.item, record.index, { silent: true, source: `wishlist-page-${this.config.sectionId}` });
      this.suppressNextChange = false;
      if (restored && record.card) {
        if (record.cachedResult) this.results.set(String(record.item.id), record.cachedResult);
        if (record.nextSibling && record.nextSibling.parentNode === record.parent) record.parent.insertBefore(record.card, record.nextSibling);
        else if (record.parent && record.parent.isConnected) record.parent.appendChild(record.card);
        else this.grid.appendChild(record.card);
      }
      const count = this.getItems().length;
      this.updateCount(count);
      this.setState(count > 0 ? 'has-items' : 'empty', count, 0);
      this.pendingUndo = null;
      if (this.undo) this.undo.hidden = true;
    }

    openClearDialog(trigger) {
      if (!this.clearDialog) return;
      const count = this.getItems().length;
      if (!count) return;
      this.dialogTrigger = trigger;
      if (this.clearMessage) this.clearMessage.textContent = String(this.texts.clearConfirmation || '').replace('{count}', String(count));
      if (typeof this.clearDialog.showModal === 'function') this.clearDialog.showModal();
      else this.clearDialog.setAttribute('open', '');
      this.clearDialog.querySelector('[data-koh-wishlist-clear-cancel]')?.focus();
    }

    closeClearDialog(returnFocus) {
      if (!this.clearDialog) return;
      if (typeof this.clearDialog.close === 'function' && this.clearDialog.open) this.clearDialog.close();
      else this.clearDialog.removeAttribute('open');
      if (returnFocus && this.dialogTrigger && this.dialogTrigger.isConnected) this.dialogTrigger.focus();
      this.dialogTrigger = null;
    }

    confirmClear() {
      this.suppressNextChange = true;
      window.KohWishlist.clear({ silent: true, source: `wishlist-page-${this.config.sectionId}` });
      this.suppressNextChange = false;
      this.cache.clear();
      this.results.clear();
      this.grid.replaceChildren();
      this.dismissUndo();
      this.setState('empty', 0, 0);
      this.closeClearDialog(false);
    }

    revealEditorBlock(block) {
      if (!block) return;
      block.hidden = false;
      window.requestAnimationFrame(() => block.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }

    pauseAutoplayForReducedMotion() {
      if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.page.querySelectorAll('video[data-koh-wishlist-autoplay-video="true"]').forEach((video) => {
        video.autoplay = false;
        video.pause();
      });
      this.page.querySelectorAll('iframe[data-koh-wishlist-autoplay-video="true"]').forEach((iframe) => {
        try {
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          iframe.contentWindow?.postMessage({ method: 'pause' }, '*');
        } catch (error) {
          // Cross-origin media errors must not interrupt the Wishlist page.
        }
      });
    }
  }

  const initializePage = (page) => {
    if (!(page instanceof Element) || instances.has(page)) return;
    const config = parseConfig(page);
    if (!config) {
      page.dataset.kohWishlistState = 'error';
      return;
    }
    const instance = new WishlistPage(page, config);
    instances.set(page, instance);
    instance.initialize();
  };

  const initializeAll = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(PAGE_SELECTOR)) initializePage(scope);
    scope.querySelectorAll(PAGE_SELECTOR).forEach(initializePage);
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target && event.target.parentElement;
    const page = target && target.closest(PAGE_SELECTOR);
    const instance = page && instances.get(page);
    if (instance) instance.handleClick(event, target);
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const page = target && target.closest(PAGE_SELECTOR);
    const instance = page && instances.get(page);
    if (instance) instance.handleControlChange(target);
  });

  document.addEventListener('koh:wishlist:change', (event) => {
    document.querySelectorAll(PAGE_SELECTOR).forEach((page) => instances.get(page)?.handleWishlistChange(event));
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const pages = [];
    if (event.target instanceof Element && event.target.matches(PAGE_SELECTOR)) pages.push(event.target);
    if (event.target && event.target.querySelectorAll) pages.push(...event.target.querySelectorAll(PAGE_SELECTOR));
    pages.forEach((page) => instances.get(page)?.destroy());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target instanceof Element ? event.target.closest('[data-koh-wishlist-block]') : null;
    const page = block && block.closest(PAGE_SELECTOR);
    const instance = page && instances.get(page);
    if (instance) instance.revealEditorBlock(block);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  else initializeAll(document);
})();
